import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { X, CheckCircle2, XCircle, RefreshCw, Send, AlertTriangle, ChevronRight, Pencil, Trash2 } from "lucide-react";

const peso = (n) => "₱" + Number(n || 0).toLocaleString("en-PH", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const dt = (s) => (s ? new Date(s).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—");
const _earliest = (vals) => { const xs = (vals || []).filter(Boolean).sort(); return xs[0] || ""; };
const _lsByKind = (o, kind) => (o.lines || []).map((l) => l.lti_source).find((x) => x && x.kind === kind) || {};
// Prefer the backend lead_time_summary (confirmed ETA + PD/ID detail from the linked inquiries);
// fall back to the per-line lti_source, and only then to the line's generic eta text.
const orderEta = (o) => (o.lead_time_summary?.eta) || _earliest((o.lines || []).map((l) => (l.lti_source || {}).eta)) || _earliest((o.lines || []).map((l) => l.eta));
const orderCutoff = (o) => (o.lead_time_summary?.po_cutoff_date) || _earliest((o.lines || []).map((l) => (l.lti_source || {}).po_cutoff_date));
const orderDownPmt = (o) => (o.lead_time_summary?.down_payment) || _lsByKind(o, "lead_time").down_payment || "";
const orderConfirmDate = (o) => { const d = (o.lead_time_summary?.confirm_date) || _lsByKind(o, "eta_qty").as_of_datetime; return d ? new Date(d).toLocaleDateString("en-PH", { timeZone: "Asia/Manila" }) : ""; };
const orderRemarksPD = (o) => (o.lead_time_summary?.remarks_pd) || _lsByKind(o, "lead_time").dept_remarks || "";
const orderRemarksID = (o) => (o.lead_time_summary?.remarks_id) || _lsByKind(o, "eta_qty").dept_remarks || "";

const STATUS = {
    draft: { label: "Draft", cls: "bg-secondary text-muted-foreground" },
    pending_user_approval: { label: "Pending Approval", cls: "bg-amber-100 text-amber-800 border border-amber-200" },
    pending_credit_approval: { label: "Credit Review", cls: "bg-orange-100 text-orange-800 border border-orange-200" },
    pending_advance_approval: { label: "Advance PO Approval", cls: "bg-sky-100 text-sky-800 border border-sky-200" },
    advance_approved: { label: "Advance PO Approved — Awaiting Stock", cls: "bg-teal-100 text-teal-800 border border-teal-200" },
    sap_posting_failed: { label: "Posting Failed", cls: "bg-rose-100 text-rose-700 border border-rose-200" },
    awaiting_ar_reserve: { label: "Awaiting AR Reserve", cls: "bg-indigo-100 text-indigo-800 border border-indigo-200" },
    completed: { label: "Completed", cls: "bg-emerald-100 text-emerald-700 border border-emerald-200" },
    rejected: { label: "Rejected", cls: "bg-rose-100 text-rose-700 border border-rose-200" },
};
const Badge = ({ status }) => {
    const s = STATUS[status] || { label: status, cls: "bg-secondary" };
    return <span className={`inline-block text-[10px] font-semibold uppercase tracking-wide px-1.5 py-0.5 rounded ${s.cls}`}>{s.label}</span>;
};

const STEP_LABEL = {
    inventory_check: "Inventory Check", user_approval: "User Approval",
    credit_validation: "Credit Validation", inquiry_dept_approval: "Inquiry-Dept Approval",
    sap_sales_order: "SAP Sales Order", tonality_allocation: "Tonality Allocation",
    ar_reserve_invoice: "AR Reserve Invoice", sap_posting: "SAP Posting", completed: "Completed", rejected: "Rejected",
    awaiting_ar_reserve: "Awaiting AR Reserve Invoice", ar_reserve_manual: "AR Reserve Invoice (manual)",
};
const stepDotCls = (st) => st === "done" || st === "approved" || st === "within_limit" ? "bg-emerald-500"
    : st === "failed" || st === "exceeds_limit" ? "bg-rose-500" : "bg-amber-400";

export default function NonOrganicOrders() {
    const { user } = useAuth();
    const canUser = user?.role === "admin" || user?.role === "branch_manager";
    const canCredit = user?.role === "admin" || user?.role === "inquiry_dept";
    const canAdvance = user?.role === "admin" || user?.role === "inquiry_dept" || user?.role === "purchasing";
    const showPrices = user?.price_visibility !== "none";
    const isApprover = canUser || canCredit || canAdvance;

    const [tab, setTab] = useState("orders");
    const [orders, setOrders] = useState([]);
    const [queue, setQueue] = useState([]);
    const [loading, setLoading] = useState(true);
    const [openId, setOpenId] = useState(null);

    const load = async () => {
        setLoading(true);
        try {
            const reqs = [api.get("/non-organic/orders")];
            if (isApprover) reqs.push(api.get("/non-organic/approvals"));
            const [o, q] = await Promise.all(reqs);
            setOrders(Array.isArray(o.data?.orders) ? o.data.orders : []);
            if (q) setQueue(Array.isArray(q.data?.queue) ? q.data.queue : []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

    return (
        <div className="p-3 md:p-6 space-y-5" data-testid="non-organic-orders-page">
            <div>
                <div className="sc-section-title">Non-Organic</div>
                <h1 className="text-xl md:text-2xl font-semibold tracking-tight">Orders</h1>
                <p className="text-sm text-muted-foreground mt-1">PO-first orders that post straight to SAP with no approval — SAP credit control is the gate (Sales Order + AR Reserve Invoice).</p>
            </div>

            <div className="inline-flex rounded-md border border-border overflow-hidden">
                <button onClick={() => setTab("orders")} className={`px-3 py-1.5 text-sm ${tab === "orders" ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}>All Orders</button>
                {isApprover && (
                    <button onClick={() => setTab("approvals")} className={`px-3 py-1.5 text-sm border-l border-border ${tab === "approvals" ? "bg-secondary font-medium" : "text-muted-foreground hover:bg-secondary/50"}`}>
                        Approvals{queue.length ? ` (${queue.length})` : ""}
                    </button>
                )}
            </div>

            {tab === "orders" && (
                <div className="sc-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full sc-table min-w-[1280px]">
                            <thead><tr><th>Order</th><th>Customer</th><th>PO No.</th><th>ETA</th><th>PO Cut-off</th><th>Down Pmt</th><th>Confirm Date</th><th>Remarks (PD)</th><th>Remarks (ID)</th><th className="text-right">Amount</th><th>Status</th><th>Created</th></tr></thead>
                            <tbody>
                                {loading && <tr><td colSpan={12} className="text-center py-8 text-muted-foreground">Loading…</td></tr>}
                                {!loading && orders.length === 0 && <tr><td colSpan={12} className="text-center py-8 text-muted-foreground">No orders yet. Build a cart and click “Create Order / PO”.</td></tr>}
                                {!loading && orders.map((o) => (
                                    <tr key={o.id} className="cursor-pointer group hover:bg-secondary/40" onClick={() => setOpenId(o.id)}>
                                        <td className="font-mono text-xs">{o.doc_no}
                                            {o.has_advance && <span className="ml-2 text-[10px] px-1 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200">ADVANCE</span>}
                                        </td>
                                        <td>{o.customer_name || o.customer_code}</td>
                                        <td className="font-mono text-xs">{o.po_no || "—"}</td>
                                        <td className="text-xs">{orderEta(o) || "—"}</td>
                                        <td className="text-xs">{orderCutoff(o) || "—"}</td>
                                        <td className="text-xs">{orderDownPmt(o) || "—"}</td>
                                        <td className="text-xs whitespace-nowrap">{orderConfirmDate(o) || "—"}</td>
                                        <td className="text-xs text-muted-foreground max-w-[150px] truncate" title={orderRemarksPD(o)}>{orderRemarksPD(o) || "—"}</td>
                                        <td className="text-xs text-muted-foreground max-w-[150px] truncate" title={orderRemarksID(o)}>{orderRemarksID(o) || "—"}</td>
                                        <td className="sc-num font-semibold">{showPrices ? peso(o.subtotal) : "—"}</td>
                                        <td><Badge status={o.status} />{o.status === "advance_approved" && o.stock_arrived_detected ? <span className="ml-1 text-[10px] px-1 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200 whitespace-nowrap">STOCK ARRIVED</span> : null}</td>
                                        <td className="text-xs text-muted-foreground whitespace-nowrap">{dt(o.created_at)}<ChevronRight className="w-3.5 h-3.5 inline ml-1 opacity-0 group-hover:opacity-100" /></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {tab === "approvals" && (
                <div className="sc-card overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="w-full sc-table min-w-[1280px]">
                            <thead><tr><th>Order</th><th>Customer</th><th>PO No.</th><th>ETA</th><th>PO Cut-off</th><th>Down Pmt</th><th>Confirm Date</th><th>Remarks (PD)</th><th>Remarks (ID)</th><th className="text-right">Amount</th><th>Stage</th><th></th></tr></thead>
                            <tbody>
                                {queue.length === 0 && <tr><td colSpan={12} className="text-center py-8 text-muted-foreground">Nothing awaiting your approval.</td></tr>}
                                {queue.map((o) => (
                                    <tr key={o.id} className="cursor-pointer hover:bg-secondary/40" onClick={() => setOpenId(o.id)}>
                                        <td className="font-mono text-xs">{o.doc_no}</td>
                                        <td>{o.customer_name || o.customer_code}</td>
                                        <td className="font-mono text-xs">{o.po_no || "—"}</td>
                                        <td className="text-xs">{orderEta(o) || "—"}</td>
                                        <td className="text-xs">{orderCutoff(o) || "—"}</td>
                                        <td className="text-xs">{orderDownPmt(o) || "—"}</td>
                                        <td className="text-xs whitespace-nowrap">{orderConfirmDate(o) || "—"}</td>
                                        <td className="text-xs text-muted-foreground max-w-[150px] truncate" title={orderRemarksPD(o)}>{orderRemarksPD(o) || "—"}</td>
                                        <td className="text-xs text-muted-foreground max-w-[150px] truncate" title={orderRemarksID(o)}>{orderRemarksID(o) || "—"}</td>
                                        <td className="sc-num font-semibold">{showPrices ? peso(o.subtotal) : "—"}</td>
                                        <td>{o.approval_stage === "credit_approval"
                                            ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-orange-100 text-orange-800 border border-orange-200">CREDIT (over limit)</span>
                                            : o.approval_stage === "advance_approval"
                                            ? <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-100 text-sky-800 border border-sky-200">ADVANCE PO</span>
                                            : <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-800 border border-amber-200">USER APPROVAL</span>}</td>
                                        <td className="text-right"><span className="text-xs text-primary">Open →</span></td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {openId && <OrderDrawer id={openId} user={user} canUser={canUser} canCredit={canCredit} canAdvance={canAdvance}
                onClose={() => setOpenId(null)} onChanged={load} />}
        </div>
    );
}

function OrderDrawer({ id, user, canUser, canCredit, canAdvance, onClose, onChanged }) {
    const [o, setO] = useState(null);
    const [busy, setBusy] = useState(false);
    const [poVal, setPoVal] = useState(null);
    const [editLineId, setEditLineId] = useState(null);
    const [editQty, setEditQty] = useState("");

    const fetchOne = async () => {
        try { const { data } = await api.get(`/non-organic/orders/${id}`); setO(data); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    useEffect(() => { fetchOne(); /* eslint-disable-next-line */ }, [id]);

    const act = async (path, okMsg, body) => {
        setBusy(true);
        try {
            await api.post(`/non-organic/orders/${id}/${path}`, body || {});
            toast.success(okMsg);
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            // Always re-sync with the backend, even on failure: a failed credit-approve
            // still flips the order to sap_posting_failed, so the drawer must reflect the
            // new status (Retry + SAP error) instead of leaving a stale "Approve" button.
            await fetchOne();
            onChanged();
            setBusy(false);
        }
    };
    const savePo = async () => {
        setBusy(true);
        try { await api.put(`/non-organic/orders/${id}/po`, { po_no: poVal ?? (o?.po_no || "") }); toast.success("PO number saved"); setPoVal(null); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { await fetchOne(); onChanged(); setBusy(false); }
    };
    const reject = async () => {
        const reason = window.prompt("Reason for rejection (optional):", "");
        if (reason === null) return;
        await act("reject", "Order rejected", { reason });
    };
    const recordAr = async () => {
        const inv = window.prompt("Enter the AR Reserve Invoice number created in SAP:", "");
        if (inv === null) return;
        if (!inv.trim()) { toast.error("Enter the AR Reserve Invoice number."); return; }
        await act("record-ar-reserve", "AR Reserve Invoice recorded", { invoice_no: inv.trim() });
    };
    const syncAr = async () => { await act("sync-ar-reserve", "AR Reserve Invoice synced from SAP"); };
    const startEditLine = (l) => { setEditLineId(l.line_id); setEditQty(String(l.quantity)); };
    const cancelEditLine = () => { setEditLineId(null); setEditQty(""); };
    const saveEditLine = async (lineId) => {
        const q = Number(editQty);
        if (!(q > 0)) { toast.error("Quantity must be greater than zero."); return; }
        setBusy(true);
        try {
            await api.put(`/non-organic/orders/${id}/lines/${lineId}`, { quantity: q });
            toast.success("Line updated");
            setEditLineId(null); setEditQty("");
        } catch (e) { toast.error(formatApiError(e)); }
        finally { await fetchOne(); onChanged(); setBusy(false); }
    };
    const deleteLine = async (lineId) => {
        if (!window.confirm("Remove this line from the order?")) return;
        setBusy(true);
        try { await api.delete(`/non-organic/orders/${id}/lines/${lineId}`); toast.success("Line removed"); }
        catch (e) { toast.error(formatApiError(e)); }
        finally { await fetchOne(); onChanged(); setBusy(false); }
    };
    const deleteOrder = async () => {
        if (!window.confirm(`Delete draft order ${o?.doc_no}? This cannot be undone.`)) return;
        setBusy(true);
        try {
            await api.delete(`/non-organic/orders/${id}`);
            toast.success("Order deleted");
            onChanged();
            onClose();
        } catch (e) { toast.error(formatApiError(e)); setBusy(false); }
    };

    const isOwner = o && (o.user_id === user?.id);
    const showPrices = user?.price_visibility !== "none";
    const credit = o?.credit || {};

    return (
        <div className="fixed inset-0 bg-black/40 flex justify-end z-50" onClick={onClose}>
            <div className="w-full max-w-2xl h-full bg-background overflow-y-auto border-l border-border" onClick={(e) => e.stopPropagation()} data-testid="non-organic-order-drawer">
                <div className="px-4 py-3 border-b border-border sticky top-0 bg-background z-10 flex items-center justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Non-Organic Order</div>
                        <div className="text-lg font-semibold font-mono">{o?.doc_no || "…"}</div>
                    </div>
                    <div className="flex items-center gap-2">{o && <Badge status={o.status} />}
                        <button onClick={onClose} className="p-1 rounded hover:bg-secondary text-muted-foreground"><X className="w-4 h-4" /></button>
                    </div>
                </div>

                {!o ? <div className="p-8 text-center text-muted-foreground">Loading…</div> : (
                    <div className="p-4 space-y-4">
                        <div className="grid grid-cols-2 gap-3">
                            <Info label="Customer" value={`${o.customer_name || ""} (${o.customer_code})`} />
                            <Info label="Company / Schema" value={o.schema_name} />
                            <Info label="Amount" value={showPrices ? peso(o.subtotal) : "—"} />
                            <Info label="Stock" value={o.has_advance ? "Includes Advance-PO lines" : "All available"} />
                            <Info label="SO Type" value={o.so_type || "—"} />
                            {(["not_posted", "sap_posting_failed"].includes(o.sap_status || "not_posted") && !o.sap_so_docnum) ? (
                                <div>
                                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Store PO No.</div>
                                    <div className="flex items-center gap-1.5 mt-1">
                                        <input className="sc-input h-8 text-sm flex-1" value={poVal ?? (o.po_no || "")} onChange={(e) => setPoVal(e.target.value)} placeholder="Store PO number" />
                                        <button className="h-8 px-2 rounded-md bg-primary text-primary-foreground text-xs disabled:opacity-50" disabled={busy} onClick={savePo}>Save</button>
                                    </div>
                                </div>
                            ) : <Info label="Store PO No." value={o.po_no || "—"} />}
                            {o.replacement_po_no ? <Info label="Replacement to PO" value={o.replacement_po_no} /> : null}
                        </div>

                        {showPrices && credit.checked_at && (
                            <div className={`rounded-md border p-3 ${credit.within_limit ? "border-emerald-200 bg-emerald-500/5" : "border-orange-300 bg-orange-500/5"}`}>
                                <div className="text-xs font-semibold mb-1">{credit.combined ? "Combined Credit · Dwellstone + Mozzaico" : "Credit Line Validation"} — {credit.within_limit ? "Within limit" : "Exceeds limit"}</div>
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs">
                                    <div><div className="text-muted-foreground">{credit.combined ? "Combined limit" : "Credit limit"}</div><div className="font-medium">{peso(credit.credit_line)}</div></div>
                                    <div><div className="text-muted-foreground">Available</div><div className="font-medium">{peso(credit.available)}</div></div>
                                    <div><div className="text-muted-foreground">This order</div><div className="font-medium">{peso(credit.order_total)}</div></div>
                                    <div><div className="text-muted-foreground">After this PO</div><div className="font-medium">{peso((Number(credit.available) || 0) - (Number(credit.order_total) || 0))}</div></div>
                                </div>
                                {credit.by_company && (
                                    <div className="mt-2 pt-2 border-t border-muted-foreground/20 grid grid-cols-1 sm:grid-cols-2 gap-1.5 text-[11px] text-muted-foreground">
                                        {["DWELLSTONE", "MOZZAICO"].map((k) => credit.by_company[k] && (
                                            <div key={k}>{k.charAt(0) + k.slice(1).toLowerCase()}: limit {peso(credit.by_company[k].credit_line)} · used {peso(credit.by_company[k].exposure)}</div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        {o.status === "sap_posting_failed" && (
                            <div className="rounded-md border border-rose-300 bg-rose-500/5 p-3">
                                <div className="flex items-center gap-1.5 text-xs font-semibold text-rose-700 mb-1"><AlertTriangle className="w-3.5 h-3.5" /> SAP posting failed</div>
                                <div className="text-xs text-muted-foreground break-words">{o.sap_error}</div>
                                {/already used|NumAtCard|-1116/i.test(o.sap_error || "") && <div className="text-xs mt-1 text-amber-700">This Store PO No. is already used in SAP. Change the <b>Store PO No.</b> above and Save, then Repost.</div>}
                                {!credit.within_limit && <div className="text-xs mt-1 text-muted-foreground">Over-limit orders are blocked by SAP’s credit control. Raise the customer’s SAP credit limit (or clear balance), then Repost.</div>}
                            </div>
                        )}

                        {o.status === "awaiting_ar_reserve" && (
                            <div className="rounded-md border border-indigo-300 bg-indigo-500/5 p-3 space-y-2">
                                <div className="text-xs font-semibold text-indigo-700">Sales Order posted — create the AR Reserve Invoice in SAP</div>
                                <div className="text-xs text-muted-foreground">The Sales Order (#{o.sap_so_docnum || "—"}) is posted. The app does not draw stock: create the AR Reserve Invoice manually in SAP, then record its number to complete the order.</div>
                                <div className="rounded border border-indigo-200 bg-background p-2">
                                    <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold mb-1">Declared tonality (select these batches when creating the AR Reserve Invoice in SAP)</div>
                                    <table className="w-full text-xs">
                                        <thead><tr className="text-muted-foreground text-left"><th className="font-medium">Item</th><th className="font-medium">Whse</th><th className="font-medium">Batch / Tonality</th><th className="font-medium text-right">Qty</th></tr></thead>
                                        <tbody>
                                            {(o.lines || []).map((l) => {
                                                const abs = (l.allocation && l.allocation.allocated_batches) || [];
                                                return (
                                                    <tr key={l.line_id} className="border-t border-indigo-100 align-top">
                                                        <td className="pr-2"><span className="font-mono">{l.item_code}</span> <span className="text-muted-foreground">{l.item_name}</span></td>
                                                        <td className="font-mono">{l.warehouse_code || "WH1"}</td>
                                                        <td className="font-mono">{abs.length ? abs.map((b) => `${b.batch_code || b.tonality} ×${Number(b.quantity || 0)}`).join(", ") : <span className="text-amber-700">no tonality declared</span>}</td>
                                                        <td className="text-right sc-num">{Number(l.quantity || 0)}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    <div className="text-[10px] text-muted-foreground mt-1">This is the tonality reserved for the dealer in the Sales Hub. Batches are assigned on the AR Reserve Invoice, not the Sales Order, which is why the SO shows none.</div>
                                </div>
                            </div>
                        )}

                        {(o.sap_so_docnum || o.sap_invoice_docnum) && (
                            <div className="grid grid-cols-2 gap-3">
                                <Info label="SAP Sales Order #" value={o.sap_so_docnum || "—"} mono />
                                <Info label="AR Reserve Invoice #" value={o.sap_invoice_docnum || "—"} mono />
                            </div>
                        )}

                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Process</div>
                            <div className="space-y-1.5">
                                {(o.process || []).map((s, i) => (
                                    <div key={i} className="flex items-center gap-2 text-sm">
                                        <span className={`w-2 h-2 rounded-full ${stepDotCls(s.status)}`} />
                                        <span className="font-medium">{STEP_LABEL[s.step] || s.step}</span>
                                        <span className="text-xs text-muted-foreground">— {s.status}{s.doc_num ? ` #${s.doc_num}` : ""}{(s.available !== undefined && showPrices) ? ` (avail ${peso(s.available)})` : ""}</span>
                                        <span className="ml-auto text-[10px] text-muted-foreground">{dt(s.at)}</span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {o.lead_time_eta_context && (() => {
                            const c = o.lead_time_eta_context;
                            return (
                                <div className="rounded-md border border-sky-200 bg-sky-500/5 p-3 space-y-2">
                                    <div className="text-[10px] uppercase tracking-wider text-sky-800">
                                        Lead Time / ETA Basis — {c.doc_no} ({c.kind === "lead_time" ? "Lead Time Inquiry" : "ETA & QTY Verification"})
                                    </div>
                                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 text-sm">
                                        <Info label="PO No." value={o.po_no || "—"} mono />
                                        <Info label="ETA" value={c.eta || "—"} />
                                        {c.kind === "lead_time" && <Info label="PO Cut-off" value={c.po_cutoff_date || "—"} />}
                                        {c.kind === "lead_time" && <Info label="Down Payment" value={c.down_payment || "—"} />}
                                        {c.kind === "eta_qty" && <Info label="Confirmation" value={c.confirmation || "—"} />}
                                        {c.kind === "eta_qty" && <Info label="Confirm As-of" value={dt(c.as_of_datetime)} />}
                                    </div>
                                    <div className="text-xs">
                                        <span className="text-muted-foreground">Remarks ({c.remarks_by}):</span> {c.remarks || "—"}
                                    </div>
                                </div>
                            );
                        })()}

                        <div>
                            <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-2">Lines</div>
                            <div className="overflow-x-auto">
                                <table className="w-full sc-table">
                                    <thead><tr><th>Item</th><th className="text-right">Qty</th><th className="text-right">Boxes</th><th className="text-right">Pcs</th><th className="text-right">Weight</th><th className="text-right">Price</th><th className="text-right">Total</th><th>Tonality</th><th>Stock</th>{o.status === "draft" && (isOwner || user?.role === "admin") && <th></th>}</tr></thead>
                                    <tbody>
                                        {(o.lines || []).map((l) => (
                                            <tr key={l.line_id}>
                                                <td><div className="font-mono text-xs">{l.item_code}</div><div className="text-xs text-muted-foreground">{l.item_name}</div></td>
                                                <td className="sc-num">
                                                    {editLineId === l.line_id
                                                        ? <input type="number" min="1" className="sc-input h-7 w-20 text-right" value={editQty} onChange={(e) => setEditQty(e.target.value)} autoFocus />
                                                        : l.quantity}
                                                </td>
                                                <td className="sc-num">{Number(l.boxes ?? l.estimated_boxes ?? 0)}</td>
                                                <td className="sc-num">{Number(l.loose_pcs || 0)}</td>
                                                <td className="sc-num">{Number(l.total_weight || 0).toFixed(2)} kg</td>
                                                <td className="sc-num">{showPrices ? peso(l.unit_price) : "—"}</td>
                                                <td className="sc-num">{showPrices ? peso(l.line_total) : "—"}</td>
                                                <td className="text-[10px] text-emerald-700 whitespace-nowrap">{(o.status === "completed" || o.sap_invoice_docnum || o.sap_so_docnum)
                                                    ? ((l.allocation && l.allocation.allocated_batches && l.allocation.allocated_batches.length) ? l.allocation.allocated_batches.map((b) => `${b.batch_code || b.tonality} ×${Number(b.quantity || 0)}`).join(", ") : "—")
                                                    : <span className="text-muted-foreground">— (after SAP)</span>}</td>
                                                <td>{l.is_advance ? <span className="text-[10px] px-1 py-0.5 rounded bg-sky-100 text-sky-700 border border-sky-200">Advance{l.eta ? ` · ETA ${l.eta}` : ""}</span> : <span className="text-xs text-emerald-600">Available</span>}</td>
                                                {o.status === "draft" && (isOwner || user?.role === "admin") && (
                                                    <td className="whitespace-nowrap">
                                                        {editLineId === l.line_id ? (
                                                            <div className="flex items-center gap-1">
                                                                <button disabled={busy} className="text-xs px-1.5 py-0.5 rounded bg-primary text-primary-foreground" onClick={() => saveEditLine(l.line_id)}>Save</button>
                                                                <button disabled={busy} className="text-xs px-1.5 py-0.5 rounded border border-border" onClick={cancelEditLine}>Cancel</button>
                                                            </div>
                                                        ) : (
                                                            <div className="flex items-center gap-1">
                                                                <button disabled={busy} title="Edit quantity" className="p-1 rounded hover:bg-secondary" onClick={() => startEditLine(l)}><Pencil className="w-3.5 h-3.5" /></button>
                                                                <button disabled={busy} title="Remove line" className="p-1 rounded hover:bg-secondary text-rose-600" onClick={() => deleteLine(l.line_id)}><Trash2 className="w-3.5 h-3.5" /></button>
                                                            </div>
                                                        )}
                                                    </td>
                                                )}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        </div>


                        {(o.lines || []).some((l) => l.lti_source) && (
                            <div className="sc-card p-3 md:p-4">
                                <div className="text-[11px] uppercase tracking-wider text-sky-700 font-semibold mb-2">Lead Time / ETA basis (reply from the inquiry) &middot; PO No.: <span className="font-mono text-foreground">{o.po_no || "—"}</span></div>
                                <div className="overflow-x-auto">
                                    <table className="w-full text-xs">
                                        <thead><tr className="text-muted-foreground text-left">
                                            <th className="font-medium pr-2 pb-1">Item</th>
                                            <th className="font-medium pr-2 pb-1">Source</th>
                                            <th className="font-medium pr-2 pb-1">Store Question</th>
                                            <th className="font-medium pr-2 pb-1">PO Cut-off</th>
                                            <th className="font-medium pr-2 pb-1">ETA</th>
                                            <th className="font-medium pr-2 pb-1">Down Pmt</th>
                                            <th className="font-medium pr-2 pb-1">Confirm</th>
                                            <th className="font-medium pr-2 pb-1">As of</th>
                                            <th className="font-medium pr-2 pb-1">PD / ID Remarks</th>
                                        </tr></thead>
                                        <tbody>
                                            {(o.lines || []).filter((l) => l.lti_source).map((l) => { const s = l.lti_source || {}; return (
                                                <tr key={l.line_id} className="border-t border-border align-top">
                                                    <td className="pr-2 py-1"><span className="font-mono">{l.item_code}</span> <span className="text-muted-foreground">{l.item_name}</span></td>
                                                    <td className="pr-2 py-1">{s.kind === "lead_time" ? "Lead Time (PD)" : "ETA/Qty (ID)"}<div className="text-[10px] text-muted-foreground font-mono">{s.doc_no || ""}</div></td>
                                                    <td className="pr-2 py-1">{s.store_comments || "—"}</td>
                                                    <td className="pr-2 py-1">{s.po_cutoff_date || "—"}</td>
                                                    <td className="pr-2 py-1">{s.eta || "—"}</td>
                                                    <td className="pr-2 py-1">{s.down_payment || "—"}</td>
                                                    <td className="pr-2 py-1">{s.confirmation || "—"}</td>
                                                    <td className="pr-2 py-1">{s.as_of_datetime ? new Date(s.as_of_datetime).toLocaleString("en-PH", { timeZone: "Asia/Manila" }) : "—"}</td>
                                                    <td className="pr-2 py-1">{s.dept_remarks || "—"}</td>
                                                </tr>
                                            ); })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}

                        <div className="flex flex-wrap gap-2 pt-2 border-t border-border">
                            {o.status === "draft" && (isOwner || user?.role === "admin") && (
                                <ActBtn onClick={() => act("submit", "Posting to SAP…")} busy={busy} icon={Send}>Submit and Post to SAP</ActBtn>
                            )}
                            {o.status === "draft" && (isOwner || user?.role === "admin") && (
                                <button disabled={busy} onClick={deleteOrder} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm font-medium bg-rose-50 text-rose-700 hover:bg-rose-100 disabled:opacity-60">
                                    <Trash2 className="w-3.5 h-3.5" /> Delete order
                                </button>
                            )}
                            {o.status === "pending_user_approval" && canUser && (
                                <>
                                    <ActBtn onClick={() => act("approve", "Approved — running credit check")} busy={busy} icon={CheckCircle2}>Approve</ActBtn>
                                    <ActBtn onClick={reject} busy={busy} icon={XCircle} variant="danger">Reject</ActBtn>
                                </>
                            )}
                            {o.status === "pending_credit_approval" && canCredit && (
                                <>
                                    <ActBtn onClick={() => act("credit-approve", "Credit approved — posting to SAP")} busy={busy} icon={CheckCircle2}>Approve (Inquiry-Dept)</ActBtn>
                                    <ActBtn onClick={reject} busy={busy} icon={XCircle} variant="danger">Reject</ActBtn>
                                </>
                            )}
                            {o.status === "pending_advance_approval" && canAdvance && (() => {
                                const primary = o.advance_primary || "purchasing";
                                const primaryLabel = primary === "inquiry" ? "Inquiry Dept" : "Purchasing Dept";
                                const notifyLabel = primary === "inquiry" ? "Purchasing Dept" : "Inquiry Dept";
                                const myDept = user?.role === "inquiry_dept" ? "inquiry" : user?.role === "purchasing" ? "purchasing" : null;
                                const canApproveThis = user?.role === "admin" || myDept === primary;
                                return (
                                    <>
                                        <div className="w-full text-[11px] text-muted-foreground">
                                            {o.advance_has_eta ? "Advance PO (with ETA)" : "Advance PO (no ETA) / PRF for Indent"} — approval by <b>{primaryLabel}</b>; {notifyLabel} is notified.
                                        </div>
                                        {canApproveThis ? (
                                            <ActBtn onClick={() => act("advance-approve", "Advance-PO approved")} busy={busy} icon={CheckCircle2}>
                                                Approve ({primaryLabel})
                                            </ActBtn>
                                        ) : (
                                            <span className="text-[11px] text-muted-foreground">Awaiting {primaryLabel} approval — you are notified only.</span>
                                        )}
                                        <ActBtn onClick={reject} busy={busy} icon={XCircle} variant="danger">Reject</ActBtn>
                                    </>
                                );
                            })()}
                            {o.status === "advance_approved" && (user?.role === "admin" || user?.role === "purchasing") && (
                                <>
                                    <div className="w-full text-[11px] text-muted-foreground">
                                        Approved for import. A scheduled scan checks SAP stock against the ETA and flags
                                        arrival; Purchasing confirms here and the order posts to SAP (Sales Order + AR
                                        Reserve Invoice).
                                    </div>
                                    {o.stock_arrived_detected ? (
                                        <div className="w-full text-[11px] px-2 py-1 rounded bg-emerald-50 text-emerald-700 border border-emerald-200">
                                            ✓ SAP stock on hand{o.stock_arrived_at ? ` as of ${new Date(o.stock_arrived_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })}` : ""} — ready to post.
                                            {Array.isArray(o.stock_check_detail) && o.stock_check_detail.length ? ` (${o.stock_check_detail.map((d) => `${d.item}: ${d.available}/${d.needed}`).join(", ")})` : ""}
                                        </div>
                                    ) : (o.last_stock_check_at ? (
                                        <div className="w-full text-[11px] text-muted-foreground">Last SAP stock check: {new Date(o.last_stock_check_at).toLocaleString("en-PH", { timeZone: "Asia/Manila" })} — not yet on hand.</div>
                                    ) : null)}
                                    <ActBtn onClick={() => act("advance-post", "Stock confirmed — posting to SAP…")} busy={busy} icon={Send}>
                                        Confirm Stock Arrived — Post to SAP
                                    </ActBtn>
                                    <ActBtn onClick={reject} busy={busy} icon={XCircle} variant="danger">Reject</ActBtn>
                                </>
                            )}
                            {o.status === "sap_posting_failed" && (canUser || canCredit || isOwner) && (
                                <>
                                    <ActBtn onClick={() => act("retry-posting", "Reposting to SAP…")} busy={busy} icon={RefreshCw}>Repost</ActBtn>
                                    <ActBtn onClick={reject} busy={busy} icon={XCircle} variant="danger">Cancel order</ActBtn>
                                </>
                            )}
                            {o.status === "awaiting_ar_reserve" && ["admin", "branch_manager", "inquiry_dept"].includes(user?.role) && (
                                <>
                                    <ActBtn onClick={syncAr} busy={busy} icon={RefreshCw}>Check SAP for AR Reserve</ActBtn>
                                    <ActBtn onClick={recordAr} busy={busy} icon={RefreshCw}>Record AR Reserve Invoice #</ActBtn>
                                </>
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}

function ActBtn({ onClick, busy, icon: Icon, children, variant }) {
    const base = variant === "danger"
        ? "border border-rose-300 text-rose-700 hover:bg-rose-50"
        : "bg-foreground text-background hover:opacity-90";
    return (
        <button onClick={onClick} disabled={busy} className={`h-9 inline-flex items-center gap-1.5 px-3 rounded-md text-sm font-medium disabled:opacity-50 ${base}`}>
            <Icon className="w-4 h-4" /> {children}
        </button>
    );
}

function Info({ label, value, mono }) {
    return (
        <div className="rounded-md border border-border px-3 py-2">
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
            <div className={`text-sm mt-0.5 ${mono ? "font-mono" : ""}`}>{value ?? "—"}</div>
        </div>
    );
}
